// @flow

import * as React from 'react';

import { useDispatch } from 'react-redux';

import { type DecoratedProduct, getCart } from '@gandi/cart';

import { notifyDanger } from '@gandi/notifications';

import { useTranslator } from '@gandi/translate';

import { GET } from 'gandi.redux-fetcher';

import { updateMailboxStandAloneQuantity } from 'src/MailboxCreate/modules/actions';
import type { ThunkAction, Dispatch } from 'src/types';

import type { Mailbox } from '../types';

type MailboxesInfos = {
  data: Array<$Exact<Mailbox>>,
  shouldMigrate: boolean,
  totalCount: number,
};

const fetchDomainBoxes: (
  fqdn: string,
) => ThunkAction<Promise<MailboxesInfos>> = fqdn => async dispatch => {
  const response = await dispatch(
    GET(`/mail/boxes/${fqdn}/boxes`, {
      params: {
        page: 1,
        per_page: 10,
      },
    }),
  );

  const shouldMigrate = !!parseInt(
    response.headers.get('X-Must-Migrate') || 0,
    10,
  );

  return {
    shouldMigrate,
    data: response.data,
    totalCount: parseInt(response.headers.get('X-Total-Count') || 0, 10),
  };
};

/**
 * Will, at fqdn change (most probably component mount) fetch domain boxes and return an updated list
 */
export function useDomainRenewableBoxes(fqdn: string): {
  mailboxes: MailboxesInfos['data'],
  totalCount: MailboxesInfos['totalCount'],
} | void {
  const dispatch = useDispatch();
  const [infos, setInfos] = React.useState<{
    mailboxes: MailboxesInfos['data'],
    totalCount: MailboxesInfos['totalCount'],
  } | void>();
  const fetcher = React.useCallback(async () => {
    try {
      const { data, shouldMigrate, totalCount } = await dispatch(
        fetchDomainBoxes(fqdn),
      );
      if (shouldMigrate) {
        setInfos({ mailboxes: data, totalCount });
      }
    } catch (err) {
      // fails silently
    }
  }, [dispatch, fqdn]);

  React.useEffect(() => {
    // eslint-disable-next-line no-void
    void fetcher();
  }, [fetcher]);

  return infos;
}

export function useOnCounterChange(
  product: DecoratedProduct,
  setLoading: Dispatch,
  loading: boolean,
): (quantity: number) => void {
  const dispatch = useDispatch();
  const { __ } = useTranslator();

  return (quantity: number) => {
    if (loading) {
      return;
    }
    setLoading(true);

    dispatch(
      updateMailboxStandAloneQuantity({
        mailboxProductId: product.id,
        quantity: Number(quantity),
      }),
    )
      .catch(() => {
        dispatch(
          notifyDanger({
            message: __('An error has occurred. Please try again.'),
          }),
        );
      })
      .then(() => {
        return dispatch(getCart());
      })
      .finally(() => {
        setLoading(false);
      });
  };
}
